import type { User } from 'firebase/auth';
import type { ActivityActorType, ActivityType } from '../types/typesActivityLog';
import { activityLogService } from './ActivityLogService';

export type AuditUser = {
  uid: string;
  displayName?: string | null;
  email?: string | null;
};

export type AuditActorContext = {
  actorType: ActivityActorType;
  actorRole?: string;
  storeName?: string;
};

let auditActorContext: AuditActorContext = { actorType: 'employee' };

/** נקרא מ־AuthContext כדי שכל כתיבות הלוג יקבלו actorType נכון */
export function setAuditActorContext(ctx: AuditActorContext | null | undefined): void {
  if (!ctx) {
    auditActorContext = { actorType: 'employee' };
    return;
  }
  auditActorContext = {
    actorType: ctx.actorType === 'external' ? 'external' : 'employee',
    actorRole: ctx.actorRole || undefined,
    storeName: ctx.storeName || undefined,
  };
}

export function getAuditActorContext(): AuditActorContext {
  return { ...auditActorContext };
}

export function auditUserFromAuth(user: User | null | undefined): AuditUser | null {
  if (!user?.uid) return null;
  return {
    uid: user.uid,
    displayName: user.displayName,
    email: user.email,
  };
}

const LOGIN_SESSION_KEY_PREFIX = 'activityLog:loginLogged:';

export function hasLoggedLoginThisSession(userId: string): boolean {
  try {
    return sessionStorage.getItem(`${LOGIN_SESSION_KEY_PREFIX}${userId}`) === '1';
  } catch {
    return false;
  }
}

export function markLoginLoggedThisSession(userId: string): void {
  try {
    sessionStorage.setItem(`${LOGIN_SESSION_KEY_PREFIX}${userId}`, '1');
  } catch {
    /* ignore */
  }
}

export function clearLoginLoggedThisSession(userId?: string): void {
  try {
    if (userId) {
      sessionStorage.removeItem(`${LOGIN_SESSION_KEY_PREFIX}${userId}`);
      return;
    }
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(LOGIN_SESSION_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

export function logAuditActivity(
  user: AuditUser | null | undefined,
  params: {
    activityType: ActivityType;
    description: string;
    repairId?: string;
    repairCallNumber?: string;
    previousValue?: string;
    newValue?: string;
    metadata?: Record<string, unknown>;
    actorType?: ActivityActorType;
    actorRole?: string;
    storeName?: string;
  },
): void {
  if (!user?.uid || !navigator.onLine) return;
  const ctx = getAuditActorContext();
  void activityLogService
    .logActivity({
      userId: user.uid,
      userName: user.displayName || user.email || 'משתמש לא מזוהה',
      userEmail: user.email || undefined,
      actorType: params.actorType || ctx.actorType,
      actorRole: params.actorRole || ctx.actorRole,
      storeName: params.storeName || ctx.storeName,
      activityType: params.activityType,
      description: params.description,
      repairId: params.repairId,
      repairCallNumber: params.repairCallNumber,
      previousValue: params.previousValue,
      newValue: params.newValue,
      metadata: params.metadata,
    })
    .catch((error) => {
      console.error('Error logging audit activity:', error);
    });
}

export function buildLoginAudit(params: {
  actorType: ActivityActorType;
  storeName?: string;
  actorRole?: string;
}) {
  const isExternal = params.actorType === 'external';
  const storeSuffix = params.storeName ? ` · חנות ${params.storeName}` : '';
  return {
    activityType: 'user_login' as const,
    description: isExternal
      ? `התחבר למערכת (משתמש חיצוני)${storeSuffix}`
      : 'התחבר למערכת',
    actorType: params.actorType,
    actorRole: params.actorRole,
    storeName: params.storeName,
    metadata: {
      actorType: params.actorType,
      storeName: params.storeName || undefined,
    },
  };
}

export function buildLogoutAudit(params: {
  actorType: ActivityActorType;
  storeName?: string;
  actorRole?: string;
}) {
  const isExternal = params.actorType === 'external';
  const storeSuffix = params.storeName ? ` · חנות ${params.storeName}` : '';
  return {
    activityType: 'user_logout' as const,
    description: isExternal
      ? `התנתק מהמערכת (משתמש חיצוני)${storeSuffix}`
      : 'התנתק מהמערכת',
    actorType: params.actorType,
    actorRole: params.actorRole,
    storeName: params.storeName,
    metadata: {
      actorType: params.actorType,
      storeName: params.storeName || undefined,
    },
  };
}

export function buildRepairViewedAudit(params: {
  callNumber: string;
  repairId?: string;
  customerName?: string;
  deviceModel?: string;
  status?: string;
  storeName?: string;
}) {
  const callNumber = String(params.callNumber || '').trim() || 'ללא מספר';
  const bits: string[] = [];
  if (params.customerName) bits.push(`לקוח: ${params.customerName}`);
  if (params.deviceModel) bits.push(`מכשיר: ${params.deviceModel}`);
  if (params.status) bits.push(`סטטוס: ${params.status}`);
  const detail = bits.length > 0 ? ` (${bits.join(' · ')})` : '';
  return {
    activityType: 'repair_viewed' as const,
    description: `צפה בקריאה ${callNumber}${detail}`,
    repairId: params.repairId,
    repairCallNumber: params.callNumber || undefined,
    storeName: params.storeName,
    metadata: {
      customerName: params.customerName || undefined,
      deviceModel: params.deviceModel || undefined,
      status: params.status || undefined,
      storeName: params.storeName || undefined,
    },
  };
}

const repairViewedCooldownMs = 5 * 60 * 1000;
const repairViewedAtByKey = new Map<string, number>();

/** מונע הצפה: אותה קריאה לא תירשם שוב תוך 5 דקות לאותו משתמש */
export function shouldLogRepairViewed(userId: string, repairId: string): boolean {
  const key = `${userId}:${repairId}`;
  const now = Date.now();
  const prev = repairViewedAtByKey.get(key) || 0;
  if (now - prev < repairViewedCooldownMs) return false;
  repairViewedAtByKey.set(key, now);
  return true;
}


export function buildPaymentCompletedAudit(params: {
  callNumber: string;
  repairId?: string;
  amount: number;
  paymentMethod?: string;
  gateway?: string;
  source?: string;
  kind?: 'primary' | 'supplement';
}) {
  const amountText = params.amount > 0 ? `₪${params.amount.toFixed(2)}` : '(לא ידוע)';
  const method = String(params.paymentMethod || '').trim() || 'לא צוין';
  const isSupplement = params.kind === 'supplement';
  const fromCustomerTrack = String(params.source || '').trim() === 'customer_track';
  return {
    activityType: 'payment_completed' as const,
    description: isSupplement
      ? `תוספת תשלום נקלטה — קריאה ${params.callNumber || 'ללא מספר'} (${amountText})`
      : fromCustomerTrack
        ? `הלקוח שילם דרך מעקב קריאות — קריאה ${params.callNumber || 'ללא מספר'} (${amountText})`
        : `תשלום הושלם — קריאה ${params.callNumber || 'ללא מספר'} (${amountText})`,
    repairId: params.repairId,
    repairCallNumber: params.callNumber || undefined,
    previousValue: undefined,
    newValue: amountText,
    metadata: {
      paymentAmount: params.amount,
      paymentMethod: method,
      gateway: params.gateway || undefined,
      source: params.source || 'payment_flow',
      kind: params.kind || 'primary',
    },
  };
}

export function buildPaymentRefundedAudit(params: {
  callNumber: string;
  repairId?: string;
  refundAmount: number;
  totalRefunded: number;
  chargedAmount: number;
  refundType: 'refund_full' | 'refund_partial';
}) {
  const isFull = params.refundType === 'refund_full';
  return {
    activityType: 'payment_refunded' as const,
    description: isFull
      ? `החזר מלא — קריאה ${params.callNumber || 'ללא מספר'} (₪${params.refundAmount.toFixed(2)})`
      : `החזר חלקי — קריאה ${params.callNumber || 'ללא מספר'} (₪${params.refundAmount.toFixed(2)})`,
    repairId: params.repairId,
    repairCallNumber: params.callNumber || undefined,
    previousValue: `₪${params.chargedAmount.toFixed(2)}`,
    newValue: isFull ? '₪0.00' : `₪${Math.max(0, params.chargedAmount - params.totalRefunded).toFixed(2)}`,
    metadata: {
      refundAmount: params.refundAmount,
      totalRefunded: params.totalRefunded,
      chargedAmount: params.chargedAmount,
      refundType: params.refundType,
    },
  };
}

export function buildPaymentCaptureCancelledAudit(params: {
  callNumber: string;
  repairId?: string;
  revertedFalseMark?: boolean;
}) {
  return {
    activityType: 'payment_capture_cancelled' as const,
    description: params.revertedFalseMark
      ? `בוטל סימון «שולם» — קריאה ${params.callNumber || 'ללא מספר'}`
      : `בוטלה לכידת אשראי — קריאה ${params.callNumber || 'ללא מספר'}`,
    repairId: params.repairId,
    repairCallNumber: params.callNumber || undefined,
    metadata: {
      revertedFalseMark: Boolean(params.revertedFalseMark),
    },
  };
}

export function buildPaymentLinkSentAudit(params: {
  channel: 'whatsapp' | 'email';
  callNumber: string;
  repairId?: string;
  amount: number;
  target?: string;
}) {
  const amountText = params.amount > 0 ? `₪${params.amount.toFixed(2)}` : '(לא ידוע)';
  const isWhatsApp = params.channel === 'whatsapp';
  return {
    activityType: (isWhatsApp ? 'payment_link_sent_whatsapp' : 'payment_link_sent_email') as ActivityType,
    description: isWhatsApp
      ? `קישור תשלום נשלח ב-WhatsApp — קריאה ${params.callNumber || 'ללא מספר'} (${amountText})`
      : `קישור תשלום נשלח למייל — קריאה ${params.callNumber || 'ללא מספר'} (${amountText})`,
    repairId: params.repairId,
    repairCallNumber: params.callNumber || undefined,
    newValue: amountText,
    metadata: {
      paymentAmount: params.amount,
      channel: params.channel,
      target: params.target || undefined,
      source: 'payment_flow',
    },
  };
}

export function buildRepairDeletedAudit(repair: {
  id?: string;
  callNumber?: string;
  status?: string;
  customerName?: string;
  deviceModel?: string;
  storeName?: string;
  paymentAmount?: number;
}) {
  const callNumber = String(repair.callNumber || '').trim() || 'ללא מספר';
  return {
    activityType: 'repair_deleted' as const,
    description: `נמחקה קריאת שירות ${callNumber}`,
    repairId: repair.id,
    repairCallNumber: repair.callNumber,
    metadata: {
      deletedStatus: repair.status || undefined,
      deletedCustomer: repair.customerName || undefined,
      deletedDevice: repair.deviceModel || undefined,
      deletedStore: repair.storeName || undefined,
      deletedPaymentAmount: Number(repair.paymentAmount || 0) || undefined,
    },
  };
}
